import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { travelAPI } from '../lib/api';
import { formatDate, formatCurrency, formatDateTime } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import {
  Plane,
  Train,
  Car,
  ArrowLeft,
  MapPin,
  Calendar,
  IndianRupee,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Building,
  Phone,
  Edit,
  Send,
  FileText,
  AlertTriangle,
  Banknote,
} from 'lucide-react';

const statusConfig = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-800', icon: Edit },
  l1_pending: { label: 'Pending L1 Approval', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  l1_approved: { label: 'L1 Approved', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  l1_rejected: { label: 'L1 Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
  l2_pending: { label: 'Pending L2 Approval', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  l2_approved: { label: 'L2 Approved', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
  l2_rejected: { label: 'L2 Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
  l3_pending: { label: 'Pending L3 Approval', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  l3_approved: { label: 'Fully Approved', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  l3_rejected: { label: 'L3 Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-800', icon: XCircle },
  completed: { label: 'Completed', color: 'bg-purple-100 text-purple-800', icon: CheckCircle },
};

const travelTypeConfig = {
  local_domestic: { label: 'Local (Same City)', color: 'bg-cyan-100 text-cyan-800' },
  domestic_outstation: { label: 'Domestic Outstation', color: 'bg-indigo-100 text-indigo-800' },
  international: { label: 'International', color: 'bg-orange-100 text-orange-800' },
};

const modeLabels = {
  flight: 'Flight',
  train: 'Train',
  bus: 'Bus',
  cab: 'Cab/Taxi',
  self_drive: 'Self Drive',
  other: 'Other',
};

const modeIcons = {
  flight: Plane,
  train: Train,
  bus: Car,
  cab: Car,
  self_drive: Car,
  other: MapPin,
};

export default function TravelRequestDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequest();
  }, [id]);

  const fetchRequest = async () => {
    try {
      const response = await travelAPI.getById(id);
      setRequest(response.data);
    } catch (error) {
      console.error('Error fetching travel request:', error);
      if (error.response?.status === 404) {
        navigate('/travel');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await travelAPI.submit(id);
      fetchRequest();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to submit');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64" />
        <Skeleton className="h-48" />
      </div>
    );
  }

  if (!request) {
    return <div>Request not found</div>;
  }

  const status = statusConfig[request.status] || statusConfig.draft;
  const StatusIcon = status.icon;
  const travelType = travelTypeConfig[request.travel_type];
  const isOwner = request.employee_id === user?.employee_id;
  const canEdit = isOwner && (request.status === 'draft' || request.status.includes('rejected'));
  const canSubmit = isOwner && request.status === 'draft';

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/travel')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold font-['Manrope']">{request.request_number}</h1>
              <Badge className={status.color}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {status.label}
              </Badge>
              <Badge className={travelType.color}>{travelType.label}</Badge>
            </div>
            <p className="text-muted-foreground">Created on {formatDateTime(request.created_at)}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <Button variant="outline" onClick={() => navigate(`/travel/${id}/edit`)}>
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}
          {canSubmit && (
            <Button onClick={handleSubmit}>
              <Send className="w-4 h-4 mr-2" />
              Submit for Approval
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="col-span-2 space-y-6">
          {/* Employee & Purpose */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Trip Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-semibold">{request.employee_name}</p>
                  <p className="text-sm text-muted-foreground">
                    {request.department} • {request.designation}
                  </p>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Purpose</p>
                <p>{request.purpose}</p>
              </div>

              {(request.project_code || request.client_name || request.cost_centre) && (
                <div className="grid grid-cols-3 gap-4">
                  {request.project_code && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Project Code</p>
                      <p>{request.project_code}</p>
                    </div>
                  )}
                  {request.client_name && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Client</p>
                      <p>{request.client_name}</p>
                    </div>
                  )}
                  {request.cost_centre && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-1">Cost Centre</p>
                      <p>{request.cost_centre}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Itinerary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Itinerary</CardTitle>
              <CardDescription>{request.itinerary?.length} leg(s)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {request.itinerary?.map((leg, index) => {
                  const ModeIcon = modeIcons[leg.mode_of_travel] || MapPin;
                  return (
                    <div
                      key={index}
                      className="p-4 rounded-lg border bg-muted/20"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <Badge variant="outline">Leg {leg.leg_number}</Badge>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <ModeIcon className="w-4 h-4" />
                          <span>{modeLabels[leg.mode_of_travel]}</span>
                          {leg.travel_class && (
                            <Badge variant="secondary" className="text-xs">
                              {leg.travel_class}
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3 text-lg">
                        <div className="flex-1">
                          <p className="font-semibold">{leg.from_location}</p>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(leg.departure_date)}
                            {leg.departure_time && ` at ${leg.departure_time}`}
                          </p>
                        </div>
                        <div className="text-2xl text-muted-foreground">→</div>
                        <div className="flex-1 text-right">
                          <p className="font-semibold">{leg.to_location}</p>
                          {leg.return_date && (
                            <p className="text-sm text-muted-foreground">
                              {formatDate(leg.return_date)}
                              {leg.return_time && ` at ${leg.return_time}`}
                            </p>
                          )}
                        </div>
                      </div>

                      {leg.accommodation_required && (
                        <div className="mt-3 pt-3 border-t flex items-center gap-2 text-sm">
                          <Building className="w-4 h-4 text-muted-foreground" />
                          <span>Accommodation needed</span>
                          {leg.hotel_preference && (
                            <span className="text-muted-foreground">
                              - {leg.hotel_preference}
                            </span>
                          )}
                        </div>
                      )}

                      {leg.notes && (
                        <p className="mt-2 text-sm text-muted-foreground">{leg.notes}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Estimated Costs */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <IndianRupee className="w-5 h-5" />
                Estimated Costs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Travel', value: request.estimated_costs?.travel },
                  { label: 'Accommodation', value: request.estimated_costs?.accommodation },
                  { label: 'Local Conveyance', value: request.estimated_costs?.local_conveyance },
                  { label: 'Per Diem', value: request.estimated_costs?.per_diem },
                  { label: 'Visa Fees', value: request.estimated_costs?.visa_fees },
                  { label: 'Insurance', value: request.estimated_costs?.insurance },
                  { label: 'Conference/Registration', value: request.estimated_costs?.conference_registration },
                  { label: 'Other', value: request.estimated_costs?.other },
                ]
                  .filter(item => item.value > 0)
                  .map((item, i) => (
                    <div key={i} className="flex justify-between py-2 border-b">
                      <span className="text-muted-foreground">{item.label}</span>
                      <span>₹{item.value?.toLocaleString()}</span>
                    </div>
                  ))}
              </div>
              <div className="mt-4 p-4 bg-primary/5 rounded-lg flex justify-between items-center">
                <span className="font-semibold">Total Estimated</span>
                <span className="text-2xl font-bold text-primary">
                  ₹{request.estimated_costs?.total_estimated?.toLocaleString()}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Compliance */}
          {request.travel_type === 'international' && request.compliance && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-500" />
                  Compliance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <ComplianceItem
                    label="Visa Required"
                    required={request.compliance.visa_required}
                    status={request.compliance.visa_status}
                  />
                  <ComplianceItem
                    label="Invitation Required"
                    required={request.compliance.invitation_required}
                    status={request.compliance.invitation_received ? 'received' : 'pending'}
                  />
                  <ComplianceItem
                    label="Insurance Required"
                    required={request.compliance.insurance_required}
                    status={request.compliance.insurance_status}
                  />
                  <ComplianceItem
                    label="HR Clearance"
                    required={request.compliance.hr_clearance_required}
                    status={request.compliance.hr_clearance_status}
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Approval Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Approval Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* L1 */}
                <ApprovalTimelineItem
                  level="L1"
                  label="Manager"
                  approver={request.approval_workflow?.l1_approver_name}
                  history={request.approval_history?.find(h => h.level === 'l1')}
                  status={getApprovalStatus(request, 'l1')}
                />
                {/* L2 */}
                <ApprovalTimelineItem
                  level="L2"
                  label="Department Head"
                  approver={request.approval_workflow?.l2_approver_name}
                  history={request.approval_history?.find(h => h.level === 'l2')}
                  status={getApprovalStatus(request, 'l2')}
                />
                {/* L3 */}
                <ApprovalTimelineItem
                  level="L3"
                  label="Finance"
                  approver={request.approval_workflow?.l3_approver_name}
                  history={request.approval_history?.find(h => h.level === 'l3')}
                  status={getApprovalStatus(request, 'l3')}
                />
              </div>
            </CardContent>
          </Card>

          {/* Advance Request */}
          {request.advance_request?.required && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Banknote className="w-5 h-5" />
                  Advance Request
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Amount</span>
                    <span className="font-semibold">
                      ₹{request.advance_request.amount?.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <Badge
                      className={
                        request.advance_request.status === 'released'
                          ? 'bg-green-100 text-green-800'
                          : request.advance_request.status === 'approved'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }
                    >
                      {request.advance_request.status}
                    </Badge>
                  </div>
                  {request.advance_request.justification && (
                    <div>
                      <p className="text-sm text-muted-foreground">Justification:</p>
                      <p className="text-sm">{request.advance_request.justification}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Emergency Contact */}
          {request.emergency_contact?.name && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Emergency Contact
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-medium">{request.emergency_contact.name}</p>
                <p className="text-sm text-muted-foreground">
                  {request.emergency_contact.relationship}
                </p>
                <p className="text-sm">{request.emergency_contact.phone}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function ApprovalTimelineItem({ level, label, approver, history, status }) {
  const config = {
    pending: { icon: Clock, color: 'text-yellow-500', bg: 'bg-yellow-50', line: 'bg-yellow-200' },
    approved: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50', line: 'bg-green-200' },
    rejected: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50', line: 'bg-red-200' },
    waiting: { icon: Clock, color: 'text-gray-300', bg: 'bg-gray-50', line: 'bg-gray-200' },
  };

  const { icon: Icon, color, bg, line } = config[status] || config.waiting;

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className={`p-2 rounded-full ${bg}`}>
          <Icon className={`w-4 h-4 ${color}`} />
        </div>
        <div className={`w-0.5 flex-1 ${line} mt-2`} />
      </div>
      <div className="flex-1 pb-4">
        <p className="font-medium">
          {level} - {label}
        </p>
        <p className="text-sm text-muted-foreground">{approver || 'Not assigned'}</p>
        {history && (
          <>
            <p className="text-sm mt-1">
              <span
                className={
                  history.action === 'approved'
                    ? 'text-green-600'
                    : history.action === 'rejected'
                    ? 'text-red-600'
                    : ''
                }
              >
                {history.action.charAt(0).toUpperCase() + history.action.slice(1)}
              </span>
              <span className="text-muted-foreground ml-1">
                {formatDateTime(history.timestamp)}
              </span>
            </p>
            {history.comments && (
              <p className="text-sm text-muted-foreground italic mt-1">&ldquo;{history.comments}&rdquo;</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ComplianceItem({ label, required, status }) {
  if (!required) return null;

  const statusConfig = {
    pending: { color: 'text-yellow-600', icon: Clock },
    approved: { color: 'text-green-600', icon: CheckCircle },
    confirmed: { color: 'text-green-600', icon: CheckCircle },
    received: { color: 'text-green-600', icon: CheckCircle },
    rejected: { color: 'text-red-600', icon: XCircle },
    not_applicable: { color: 'text-gray-400', icon: null },
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <div className="flex items-center gap-2">
      {Icon && <Icon className={`w-4 h-4 ${config.color}`} />}
      <span>{label}</span>
      <Badge variant="outline" className="text-xs ml-auto">
        {status}
      </Badge>
    </div>
  );
}

function getApprovalStatus(request, level) {
  const status = request.status;

  if (status === `${level}_pending`) return 'pending';
  if (status === `${level}_rejected`) return 'rejected';

  const history = request.approval_history?.find(h => h.level === level && h.action === 'approved');
  if (history) return 'approved';

  if (
    (level === 'l1' && ['l2_pending', 'l2_approved', 'l3_pending', 'l3_approved'].includes(status)) ||
    (level === 'l2' && ['l3_pending', 'l3_approved'].includes(status)) ||
    (level === 'l3' && status === 'l3_approved')
  ) {
    return 'approved';
  }

  return 'waiting';
}
